// =====================================================================
//  ADMIN CONSOLE, DOCUMENTS
// ---------------------------------------------------------------------
//  The organization's document library.
//
//  An honest note about scope: this backend stores a *link* to a file, not
//  the file itself, there is no upload endpoint and no object storage
//  configured. So this screen manages document records that point at a
//  URL, and the UI says so plainly rather than showing a drop zone that
//  would quietly do nothing.
// =====================================================================

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  FileText,
  FolderOpen,
  Link2,
  MoreHorizontal,
  Paperclip,
  Pencil,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import DataTable from '../DataTable';
import {
  Button,
  Chip,
  ConfirmDialog,
  Drawer,
  Field,
  FilterChip,
  Grid,
  Input,
  KeyValue,
  KpiTile,
  Menu,
  MenuItem,
  MenuSeparator,
  Modal,
  Notice,
  PageHeader,
  SearchInput,
  Select,
  Textarea,
  Toolbar,
  ToolbarSpacer,
  toast,
  useDebounced,
} from '../ui';
import { DASH, distinct, downloadCsv, fmtDate, fmtNumber, matchesSearch, orDash, timeAgo, toISODate } from '../format';

const CATEGORIES = ['Policy', 'Contract', 'Handbook', 'Form', 'Certificate', 'Payslip', 'Other'];

const EMPTY = {
  title: '',
  description: '',
  category: 'Policy',
  file_url: '',
  file_name: '',
  file_type: '',
  employee_id: '',
};

export default function DocumentsPage({ role, currentUser }) {
  const queryClient = useQueryClient();
  const canManage = ['admin', 'hr', 'manager'].includes(role);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [active, setActive] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);

  const debouncedSearch = useDebounced(search, 250);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'documents'],
    queryFn: () => backendApi.entities.Document.page({ limit: 500, sort: 'created_at', order: 'desc' }),
  });

  // Pinned identities so the filter/lookup memos don't recompute every render.
  const documents = useMemo(() => data?.rows || [], [data]);

  const { data: employees } = useQuery({
    queryKey: ['admin', 'people-lite'],
    queryFn: () => backendApi.entities.Employee.page({ limit: 1000, sort: 'full_name', order: 'asc' }),
  });

  const employeeRows = useMemo(() => employees?.rows || [], [employees]);
  const employeeById = useMemo(
    () => new Map(employeeRows.map((e) => [String(e.id), e])),
    [employeeRows]
  );

  /* ------------------------------ mutations --------------------------- */
  const save = useMutation({
    mutationFn: (payload) =>
      editing
        ? backendApi.entities.Document.update(editing.id, payload)
        : backendApi.entities.Document.create(payload),
    onSuccess: () => {
      toast.success(editing ? 'Document updated' : 'Document added');
      setFormOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin', 'documents'] });
    },
    onError: (err) => toast.error(err.message || 'Could not save this document'),
  });

  const remove = useMutation({
    mutationFn: (id) => backendApi.entities.Document.delete(id),
    onSuccess: () => {
      toast.success('Document removed');
      setPendingDelete(null);
      setActive(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'documents'] });
    },
    onError: (err) => toast.error(err.message || 'Could not remove this document'),
  });

  /* -------------------------------- form ------------------------------ */
  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setErrors({});
    setFormOpen(true);
  };

  const openEdit = (doc) => {
    setEditing(doc);
    setForm({
      title: doc.title || '',
      description: doc.description || '',
      category: doc.category || 'Policy',
      file_url: doc.file_url || '',
      file_name: doc.file_name || '',
      file_type: doc.file_type || '',
      employee_id: doc.employee_id ? String(doc.employee_id) : '',
    });
    setErrors({});
    setFormOpen(true);
  };

  const setField = (key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // Derive a sensible filename and type from the URL so the library
      // stays useful even when someone only pastes a link.
      if (key === 'file_url' && value && !prev.file_name) {
        try {
          const path = new URL(value).pathname;
          const name = decodeURIComponent(path.split('/').filter(Boolean).pop() || '');
          if (name) {
            next.file_name = name;
            const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
            if (ext) next.file_type = ext;
          }
        } catch {
          /* not a parseable URL yet, the user is still typing */
        }
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const submit = (event) => {
    event.preventDefault();
    const next = {};
    if (!form.title.trim()) next.title = 'Title is required';
    if (!form.file_url.trim()) next.file_url = 'A link to the document is required';
    else if (!/^https?:\/\//i.test(form.file_url.trim())) next.file_url = 'Must start with http:// or https://';
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    save.mutate({
      ...Object.fromEntries(Object.entries(form).filter(([, v]) => v !== '' && v != null)),
      uploaded_by: currentUser?.full_name || currentUser?.email || 'Administrator',
    });
  };

  /* --------------------------- derived views -------------------------- */
  const categories = useMemo(() => distinct(documents, 'category'), [documents]);

  const filtered = useMemo(
    () =>
      documents.filter((row) => {
        const owner = row.employee_id ? employeeById.get(String(row.employee_id)) : null;
        const searchable = { ...row, owner_name: owner?.full_name || '' };
        return (
          matchesSearch(searchable, debouncedSearch, [
            'title',
            'description',
            'category',
            'file_name',
            'uploaded_by',
            'owner_name',
          ]) &&
          (!categoryFilter || row.category === categoryFilter) &&
          (!scopeFilter ||
            (scopeFilter === 'Company-wide' ? !row.employee_id : Boolean(row.employee_id)))
        );
      }),
    [documents, debouncedSearch, categoryFilter, scopeFilter, employeeById]
  );

  const companyWide = documents.filter((d) => !d.employee_id).length;
  const personal = documents.length - companyWide;
  const brokenLinks = documents.filter((d) => !d.file_url).length;

  const columns = useMemo(
    () => [
      {
        key: 'title',
        label: 'Document',
        width: 280,
        render: (row) => (
          <div className="ac-cell-stack">
            <span className="ac-cell-main">{row.title || 'Untitled'}</span>
            <span className="ac-cell-sub">{row.file_name || row.description || ','}</span>
          </div>
        ),
      },
      {
        key: 'category',
        label: 'Category',
        width: 130,
        render: (row) => (row.category ? <Chip>{row.category}</Chip> : DASH),
      },
      {
        key: 'employee_id',
        label: 'Applies to',
        width: 190,
        render: (row) => {
          if (!row.employee_id) return <Chip tone="accent">Company-wide</Chip>;
          const owner = employeeById.get(String(row.employee_id));
          return owner ? owner.full_name : `Employee #${row.employee_id}`;
        },
        csv: (row) =>
          row.employee_id ? employeeById.get(String(row.employee_id))?.full_name || row.employee_id : 'Company-wide',
      },
      { key: 'uploaded_by', label: 'Added by', width: 150, render: (row) => orDash(row.uploaded_by) },
      {
        key: 'created_at',
        label: 'Added',
        width: 110,
        render: (row) => <span title={fmtDate(row.created_at)}>{timeAgo(row.created_at)}</span>,
        csv: (row) => row.created_at,
      },
      {
        key: 'file_url',
        label: 'Link',
        width: 80,
        sortable: false,
        render: (row) =>
          row.file_url ? (
            <a
              href={row.file_url}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => event.stopPropagation()}
              style={{ color: 'var(--ac-accent)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              Open
              <ExternalLink size={11} />
            </a>
          ) : (
            <Chip tone="negative">Missing</Chip>
          ),
      },
    ],
    [employeeById]
  );

  const exportCsv = () => {
    downloadCsv(`documents-${toISODate()}`, filtered, [
      { key: 'title', label: 'Title' },
      { key: 'category', label: 'Category' },
      { key: 'description', label: 'Description' },
      {
        key: 'employee_id',
        label: 'Applies to',
        csv: (r) => (r.employee_id ? employeeById.get(String(r.employee_id))?.full_name || r.employee_id : 'Company-wide'),
      },
      { key: 'uploaded_by', label: 'Added by' },
      { key: 'file_url', label: 'Link' },
      { key: 'created_at', label: 'Added at' },
    ]);
    toast.success(`Exported ${filtered.length} documents`);
  };

  return (
    <div className="ac-page ac-page--wide">
      <PageHeader
        title="Documents"
        sub="Policies, contracts and forms, company-wide or attached to one person."
        actions={
          <>
            <Button icon={Paperclip} onClick={exportCsv} disabled={filtered.length === 0}>
              Export
            </Button>
            {canManage ? (
              <Button variant="primary" icon={Plus} onClick={openCreate}>
                Add document
              </Button>
            ) : null}
          </>
        }
      />

      <Grid cols={4} style={{ marginBottom: 14 }}>
        <KpiTile label="Documents" value={fmtNumber(documents.length)} icon={FolderOpen} foot="In the library" />
        <KpiTile label="Company-wide" value={fmtNumber(companyWide)} icon={Users} foot="Visible to everyone" />
        <KpiTile label="Person-specific" value={fmtNumber(personal)} icon={FileText} foot="Attached to an employee" />
        <KpiTile
          label="Missing link"
          value={fmtNumber(brokenLinks)}
          icon={Link2}
          tone={brokenLinks > 0 ? 'warn' : 'pos'}
          foot={brokenLinks > 0 ? 'Records with no file URL' : 'All records have a link'}
        />
      </Grid>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        onRowClick={setActive}
        defaultSort={{ key: 'created_at', dir: 'desc' }}
        emptyIcon={FolderOpen}
        emptyTitle={documents.length === 0 ? 'No documents yet' : 'No matching documents'}
        emptyText={
          documents.length === 0
            ? 'Add your first policy or handbook so employees can find it.'
            : 'Adjust the search or filters above.'
        }
        rowActions={
          canManage
            ? (row) => (
                <Menu
                  align="right"
                  trigger={({ toggle }) => (
                    <button
                      type="button"
                      className="ac-iconbtn"
                      onClick={toggle}
                      aria-label="Row actions"
                      style={{ width: 24, height: 24 }}
                    >
                      <MoreHorizontal size={14} strokeWidth={2} />
                    </button>
                  )}
                >
                  <MenuItem icon={Pencil} onClick={() => openEdit(row)}>
                    Edit
                  </MenuItem>
                  <MenuSeparator />
                  <MenuItem icon={Trash2} danger onClick={() => setPendingDelete(row)}>
                    Remove
                  </MenuItem>
                </Menu>
              )
            : undefined
        }
        toolbar={
          <Toolbar>
            <SearchInput value={search} onChange={setSearch} placeholder="Search title, category, owner…" width={250} />
            <FilterChip label="Category" value={categoryFilter} options={categories} onChange={setCategoryFilter} />
            <FilterChip
              label="Scope"
              value={scopeFilter}
              options={['Company-wide', 'Person-specific']}
              onChange={setScopeFilter}
            />
            <ToolbarSpacer />
            <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>{fmtNumber(filtered.length)} documents</span>
          </Toolbar>
        }
      />

      {/* --------------------------- DRAWER --------------------------- */}
      <Drawer
        open={Boolean(active)}
        onClose={() => setActive(null)}
        title={active?.title || 'Document'}
        sub={active?.file_name}
        badge={active?.category ? <Chip>{active.category}</Chip> : null}
        footer={
          <>
            {canManage ? (
              <Button variant="danger" icon={Trash2} onClick={() => setPendingDelete(active)}>
                Remove
              </Button>
            ) : null}
            <div style={{ flex: '1 1 auto' }} />
            {active?.file_url ? (
              <Button
                iconRight={ExternalLink}
                onClick={() => window.open(active.file_url, '_blank', 'noopener,noreferrer')}
              >
                Open document
              </Button>
            ) : null}
            {canManage ? (
              <Button
                variant="primary"
                icon={Pencil}
                onClick={() => {
                  const record = active;
                  setActive(null);
                  openEdit(record);
                }}
              >
                Edit
              </Button>
            ) : null}
          </>
        }
      >
        {active ? (
          <>
            {active.description ? (
              <p style={{ margin: '0 0 18px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--ac-body)' }}>
                {active.description}
              </p>
            ) : null}

            <div className="ac-eyebrow" style={{ marginBottom: 7 }}>
              Details
            </div>
            <KeyValue
              items={[
                { key: 'Category', value: active.category ? <Chip>{active.category}</Chip> : null },
                {
                  key: 'Applies to',
                  value: active.employee_id ? (
                    employeeById.get(String(active.employee_id))?.full_name || `Employee #${active.employee_id}`
                  ) : (
                    <Chip tone="accent">Company-wide</Chip>
                  ),
                },
                { key: 'File name', value: active.file_name },
                { key: 'File type', value: active.file_type ? active.file_type.toUpperCase() : null },
                { key: 'Size', value: active.file_size },
                { key: 'Added by', value: active.uploaded_by },
                { key: 'Added on', value: fmtDate(active.created_at) },
                {
                  key: 'Link',
                  value: active.file_url ? (
                    <a
                      href={active.file_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--ac-accent)', wordBreak: 'break-all' }}
                    >
                      {active.file_url}
                    </a>
                  ) : null,
                },
              ]}
            />
          </>
        ) : null}
      </Drawer>

      {/* ---------------------------- FORM ---------------------------- */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? 'Edit document' : 'Add a document'}
        size="lg"
        footer={
          <>
            <Button onClick={() => setFormOpen(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} loading={save.isPending}>
              {editing ? 'Save changes' : 'Add document'}
            </Button>
          </>
        }
      >
        <form onSubmit={submit}>
          <Notice tone="info" icon={Link2} title="This library stores links, not files">
            Host the file somewhere your team can reach, shared drive, storage bucket, intranet, and paste the link
            below. There is no file storage configured on this deployment.
          </Notice>

          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Title" required error={errors.title}>
              <Input value={form.title} onChange={setField('title')} invalid={!!errors.title} autoFocus />
            </Field>

            <Field label="Link to document" required error={errors.file_url}>
              <Input
                type="url"
                value={form.file_url}
                onChange={setField('file_url')}
                invalid={!!errors.file_url}
                placeholder="https://drive.example.com/handbook.pdf"
              />
            </Field>

            <Grid cols={2}>
              <Field label="Category">
                <Select value={form.category} onChange={setField('category')} options={CATEGORIES} />
              </Field>
              <Field
                label="Applies to"
                hint="Leave blank to make it visible company-wide"
              >
                <Select
                  value={form.employee_id}
                  onChange={setField('employee_id')}
                  options={employeeRows.map((e) => ({ value: String(e.id), label: e.full_name }))}
                  placeholder="Everyone (company-wide)"
                />
              </Field>
              <Field label="File name">
                <Input value={form.file_name} onChange={setField('file_name')} placeholder="handbook.pdf" />
              </Field>
              <Field label="File type">
                <Input value={form.file_type} onChange={setField('file_type')} placeholder="pdf" />
              </Field>
            </Grid>

            <Field label="Description">
              <Textarea value={form.description} onChange={setField('description')} rows={3} />
            </Field>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => remove.mutate(pendingDelete.id)}
        loading={remove.isPending}
        title="Remove this document?"
        confirmLabel="Remove"
        message={
          <>
            <strong>{pendingDelete?.title}</strong> will be removed from the library. The underlying file is not
            affected, only this record pointing at it.
          </>
        }
      />
    </div>
  );
}
